/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Badge, Text } from '@change/ds-core-ui';
import { ArrowRightIcon, TrendingIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { focus } from '#src/app/shared/utils/dom';

import { useScrollToSection } from '../../../../hooks/useScrollToSection';
import type { SpotlightContent } from '../../hooks/useSpotlightCardContent/types';
import { useMomentumBarTracking } from '../../hooks/useTracking';

const CYCLE_INTERVAL_MS = 4000;

export type SpotlightCardProps = {
	items: SpotlightContent[];
	animatedCardsCarousel?: boolean;
	testId?: string;
	qaId?: string;
};

// eslint-disable-next-line complexity
export const SpotlightCard: React.FC<SpotlightCardProps> = ({ items, animatedCardsCarousel = false, testId, qaId }) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const isPausedRef = useRef(false); // true when hovered or focused (card or pill)
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();
	const {
		actions: { scrollToSection },
	} = useScrollToSection();
	const tracking = useMomentumBarTracking();
	const { translate } = useI18n();

	const safeIndex = items.length > 0 ? currentIndex % items.length : 0;
	const currentItem = items[safeIndex];
	const canCycle = animatedCardsCarousel && items.length > 1;
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	const sectionId = currentItem?.sectionId;

	const handleCardClick = useCallback(() => {
		if (sectionId) {
			tracking.trackSpotlightContentClick(petitionId);
			scrollToSection(sectionId);
			focus(document.querySelector<HTMLElement>(`#${sectionId}`), { makeFocusable: true, preventScroll: true });
		}
	}, [petitionId, scrollToSection, sectionId, tracking]);

	const handleSpotlightNavigationClick = useCallback(() => {
		tracking.trackSpotlightNavigationClick(petitionId);
	}, [petitionId, tracking]);

	const advance = useCallback(() => {
		setCurrentIndex((i) => (i + 1) % items.length);
	}, [items.length]);

	useEffect(() => {
		const interval = canCycle
			? setInterval(() => {
					if (!isPausedRef.current) advance();
				}, CYCLE_INTERVAL_MS)
			: undefined;
		return () => {
			if (interval !== undefined) clearInterval(interval);
		};
	}, [canCycle, advance]);

	return (
		<div
			data-testid={testId}
			data-qa={qaId}
			className={cn(
				// `bg-gradient-background-light-3` is not mode-invariant despite the name: its color stops crush to
				// near-transparent alpha in dark mode, which would expose the page's dark background behind this
				// card's hardcoded dark text/border below. There's no `-fixed` background token, so the card's own
				// light-mode gradient value is hardcoded here to keep it permanently light in both modes.
				'group mb-[10px] flex w-full flex-col items-start gap-3 rounded-lg border border-[#E1DDE9] bg-[linear-gradient(84deg,#FEF2F2_0.21%,#FAF8FD_45.88%,#FFFFEB_99.64%)] pt-[14px] pr-[14px] pb-[14px] pl-[14px] shadow-[0_2px_2px_rgba(0,0,0,0.05),0_3px_19px_rgba(58,41,62,0.06)] lg:mb-0 lg:w-[60%] lg:gap-6 lg:pt-[25px] lg:pr-[20px] lg:pl-[20px]',
				canCycle && 'cursor-pointer',
			)}
			onMouseEnter={() => {
				isPausedRef.current = true;
			}}
			onMouseLeave={() => {
				isPausedRef.current = false;
			}}
			onFocusCapture={() => {
				isPausedRef.current = true;
			}}
			onBlurCapture={() => {
				isPausedRef.current = false;
			}}
			onClick={canCycle ? () => advance() : undefined}
			role={canCycle ? 'button' : undefined}
			tabIndex={canCycle ? 0 : undefined}
			aria-label={canCycle ? translate('corgi.petition_details.momentum_bar.next_activity') : undefined}
			onKeyDown={
				canCycle
					? (e: React.KeyboardEvent) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								advance();
							}
						}
					: undefined
			}
		>
			{/* Top row: badge + navigation pill (pill only shown when carousel is active with multiple items) */}
			<div className="flex flex-row items-center gap-2">
				<div key={safeIndex} className={cn('shrink-0', animatedCardsCarousel && 'animate-spotlight-card-enter')}>
					<Badge
						emphasis="strong"
						label={currentItem.badgeLabel}
						size="tiny"
						variant="neutral"
						data-qa={qaId ? `${qaId}-badge` : undefined}
					/>
				</div>
				{canCycle && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							handleSpotlightNavigationClick();
							advance();
						}}
						aria-label={translate('corgi.petition_details.momentum_bar.spotlight_pagination_next')}
						data-qa={qaId ? `${qaId}-counter` : undefined}
						className={cn(
							'group/pill flex h-5 items-center gap-0.5 rounded-full',
							'border border-[#E1DDE9] px-[10px] py-[9px]',
							'text-xs leading-5 font-light text-[#766F81]',
							'transition-[colors,transform,border,width]',
						)}
					>
						<Text variant="body" size="x-small" className="tabular-nums">
							{safeIndex + 1}/{items.length}
						</Text>
						<ArrowRightIcon
							size="x-small"
							className={cn(
								'hidden -translate-x-1 opacity-0',
								'duration-t2 transition-[opacity,transform] ease-out',
								'group-hover/pill:block group-hover/pill:translate-x-0 group-hover/pill:opacity-100',
							)}
						/>
					</button>
				)}
			</div>
			<div key={`text-${safeIndex}`} className={cn('w-full', animatedCardsCarousel && 'animate-spotlight-card-enter')}>
				{sectionId ? (
					<button
						type="button"
						className="mb-0 flex w-full cursor-pointer items-center gap-2 rounded border-0 bg-transparent p-0 text-left font-semibold text-[#403A49] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#403A49] focus-visible:ring-offset-2 lg:mb-[14px]"
						onClick={(e) => {
							e.stopPropagation();
							handleCardClick();
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								e.stopPropagation();
								handleCardClick();
							}
						}}
						aria-label={translate('corgi.petition_details.momentum_bar.go_to_section', {
							section: currentItem.badgeLabel,
						})}
						data-qa={qaId ? `${qaId}-label` : undefined}
					>
						<Text variant="body" size="large" className="mb-0 flex items-center gap-2 font-semibold text-[#403A49]">
							{currentItem.showIcon ? <TrendingIcon size="small" /> : null}
							{currentItem.relativeTime !== undefined ? (
								<span className="min-w-0 text-left">
									<span className="group-hover:underline">{currentItem.text}</span>{' '}
									<span className="ml-1 font-light group-hover:underline">{currentItem.relativeTime}</span>
								</span>
							) : (
								<span className="group-hover:underline">{currentItem.text}</span>
							)}
						</Text>
					</button>
				) : (
					<Text
						variant="body"
						size="large"
						className="mb-0 flex items-center gap-2 font-semibold text-[#403A49] lg:mb-[14px]"
						data-qa={qaId ? `${qaId}-label` : undefined}
					>
						{currentItem.showIcon ? <TrendingIcon size="small" /> : null}
						{currentItem.text}
					</Text>
				)}
			</div>
		</div>
	);
};
